import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  TextField,
  InputAdornment,
  IconButton,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
  Switch,
  Stack,
  Select,
  FormControl,
  InputLabel,
  Alert,
  CircularProgress,
  FormControlLabel,
  Chip,
  Tooltip,
  Badge,
  Divider,
  ToggleButton,
  ToggleButtonGroup,
  Paper,
  Fade,
  Zoom,
  Card as MuiCard,
  CardActions,
  TablePagination,
} from '@mui/material';
import { Card } from '../components/Card';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Lock as LockIcon,
  Public as PublicIcon,
  Delete as DeleteIcon,
  FilterList as FilterIcon,
  Tag as TagIcon,
  TextFields as TextFieldsIcon,
  Folder as FolderIcon,
  Description as FileIcon,
  Group as GroupIcon,
  ViewModule as GridViewIcon,
  ViewList as ListViewIcon,
  Sort as SortIcon,
  Clear as ClearIcon,
  Schedule as ScheduleIcon,
  Business as BusinessIcon,
} from '@mui/icons-material';
import { formatDate } from '../utils/mockData';
import { formatBytes } from '../utils/formatters';
import { Project, User, ProjectStage, ProjectPriority, ProjectSort } from '../types';
import { getProjects, createProject, deleteProject, getOrganizationUsers, addUserToProject, getProjectStageDescriptions } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { ProjectCardSkeleton, PageHeaderSkeleton } from '../components/LoadingSkeleton';

interface StageDescription {
  [key: string]: string;
}

const toTitleCase = (str: string) => {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1);
};

const Projects: React.FC = () => {
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  
  // Enhanced UI state
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [sortBy, setSortBy] = useState<ProjectSort>(ProjectSort.UPDATED_DESC);
  const [filterStage, setFilterStage] = useState<ProjectStage | 'all'>('all');
  const [stageDescriptions, setStageDescriptions] = useState<StageDescription>({});
  const [filterPriority, setFilterPriority] = useState<ProjectPriority | 'all'>('all');
  const [filterPrivacy, setFilterPrivacy] = useState<'all' | 'private' | 'public'>('all');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalProjects, setTotalProjects] = useState(0);
  
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [isCreatingProject, setIsCreatingProject] = useState(false);
  const [newProjectName, setNewProjectName] = useState('');
  const [newProjectDescription, setNewProjectDescription] = useState('');
  const [newProjectTags, setNewProjectTags] = useState<string[]>([]);
  const [newProjectIsPrivate, setNewProjectIsPrivate] = useState(true);
  const [selectedTeamMembers, setSelectedTeamMembers] = useState<User[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [loadingUsers, setLoadingUsers] = useState<boolean>(false);
  const [newProjectStage, setNewProjectStage] = useState<ProjectStage>(ProjectStage.Origination);
  const [newProjectPriority, setNewProjectPriority] = useState<ProjectPriority>(ProjectPriority.Medium);
  const [validationErrors, setValidationErrors] = useState<{ [key: string]: string }>({});

  useEffect(() => {
    const fetchStageDescriptions = async () => {
      try {
        const data = await getProjectStageDescriptions();
        setStageDescriptions(data);
      } catch (err) {
        console.error('Error fetching stage descriptions:', err);
      }
    };

    fetchStageDescriptions();
  }, []);

  useEffect(() => {
    let isMounted = true;
    const fetchProjects = async () => {
      try {
        if (!isMounted) return;
        setLoading(true);
        setError(null);
        const data = await getProjects({
          page: page + 1,
          size: rowsPerPage,
          sort: sortBy,
          stage: filterStage !== 'all' ? [filterStage] : undefined,
          priority: filterPriority !== 'all' ? [filterPriority] : undefined,
          is_private: filterPrivacy !== 'all' ? filterPrivacy === 'private' : undefined,
          search: searchTerm,
        });
        if (!isMounted) return;
        if (!data || !data.projects) {
          throw new Error('Invalid response format');
        }
        setTotalProjects(data.total || 0);
        const transformedProjects = data.projects.map((project: any) => ({
          ...project,
          id: project.id,
          name: project.name || 'Untitled Project',
          description: project.description || '',
          isPrivate: project.is_private ?? true,
          tags: project.tags || [],
          fileCount: project.file_count || 0,
          queryCount: project.query_count || 0,
          totalSize: project.total_size || 0,
          updatedDate: project.updated_at || new Date().toISOString(),
          stage: project.stage || 'Initiation',
          priority: project.priority || 'Medium',
        }));
        if (isMounted) {
          setProjects(transformedProjects);
        }
      } catch (err: any) {
        if (isMounted) {
          const errorMessage =
            err?.response?.data?.message ||
            err?.message ||
            'Failed to fetch projects. Please check your connection.';
          setError(errorMessage);
          console.error('Error fetching projects:', err);
          setProjects([]);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };
    fetchProjects();
    return () => {
      isMounted = false;
    };
  }, [sortBy, filterStage, filterPriority, filterPrivacy, searchTerm, page, rowsPerPage]);

  useEffect(() => {
    const fetchUsers = async () => {
      if (!createDialogOpen) return;
      
      try {
        setLoadingUsers(true);
        const storedOrg = localStorage.getItem('organization');
        if (!storedOrg) {
          console.warn('Organization not found in local storage');
          setUsers([]);
          return;
        }
        
        let organization_id: string | null = null;
        try {
          const orgData = JSON.parse(storedOrg);
          organization_id = orgData.id;
        } catch (parseError) {
          console.error('Error parsing organization data:', parseError);
          setUsers([]);
          return;
        }
        
        if (!organization_id) {
          console.warn('Organization ID not found');
          setUsers([]);
          return;
        }
        
        const data = await getOrganizationUsers(organization_id);
        
        let membersArray;
        if (Array.isArray(data)) {
          membersArray = data;
        } else if (data && data.members && Array.isArray(data.members)) {
          membersArray = data.members;
        } else {
          console.warn('Invalid users data format:', data);
          setUsers([]);
          return;
        }
        
        const transformedUsers = membersArray.map((member: any) => ({
          id: member.user_id || member.id || '',
          name: member.user_name || (member.user?.name) || member.name || 'Unknown User',
          email: member.user_email || (member.user?.email) || member.email || '',
          avatar: member.user_avatar_url || (member.user?.avatar_url) || member.avatar_url || member.avatar || '',
          is_active: member.is_active ?? true,
          organization_id: organization_id,
          roles: member.user?.role ? [member.user.role] : (member.role ? [member.role] : []),
        }));
        
        const activeTransformedUsers = transformedUsers.filter((user: User) => user.is_active);
        setUsers(activeTransformedUsers);
        
        if (currentUser) {
          const currentUserInList = activeTransformedUsers.find((u: User) => u.id === currentUser.id);
          if (currentUserInList) {
            setSelectedTeamMembers([currentUserInList]);
          } else {
            // If current user is not in the org list, add them manually
            setSelectedTeamMembers([currentUser]);
          }
        }
      } catch (err: any) {
        console.error('Error fetching users:', err);
        setUsers([]);
      } finally {
        setLoadingUsers(false);
      }
    };

    fetchUsers();
  }, [createDialogOpen, currentUser]);

  const allTags = useMemo(() => {
    const tags = new Set<string>();
    projects.forEach(project => {
      project.tags.forEach(tag => tags.add(tag));
    });
    return Array.from(tags).sort();
  }, [projects]);

  const filteredProjects = useMemo(() => {
    // API handles search, stage, priority, privacy. UI handles tags filter for now.
    return projects.filter((project: Project) => {
      if (selectedTags.length === 0) return true;
      return selectedTags.every(tag => project.tags.includes(tag));
    });
  }, [projects, selectedTags]);

  const clearAllFilters = () => {
    setSearchTerm('');
    setFilterStage('all');
    setFilterPriority('all');
    setFilterPrivacy('all');
    setSelectedTags([]);
  };

  const hasActiveFilters = searchTerm || filterStage !== 'all' || filterPriority !== 'all' ||
    filterPrivacy !== 'all' || selectedTags.length > 0;

  const getStageColor = (stage: ProjectStage | undefined) => {
    if (!stage) return 'default';
    switch (stage) {
      case ProjectStage.Initiation: return 'info';
      case ProjectStage.Planning: return 'warning';
      case ProjectStage.Execution: return 'primary';
      case ProjectStage.Monitoring: return 'success';
      case ProjectStage.Closure: return 'default';
      default: return 'default';
    }
  };

  const getPriorityColor = (priority: ProjectPriority | undefined) => {
    if (!priority) return 'default';
    switch (priority) {
      case ProjectPriority.Low: return 'success';
      case ProjectPriority.Medium: return 'info';
      case ProjectPriority.High: return 'warning';
      case ProjectPriority.Critical: return 'error';
      default: return 'default';
    }
  };

  const validate = () => {
    const errors: { [key: string]: string } = {};
    if (!newProjectName.trim()) {
      errors.name = 'Project name is required.';
    }
    return errors;
  };

  useEffect(() => {
    if (!createDialogOpen) {
      setValidationErrors({});
    }
  }, [createDialogOpen]);

  const handleCreateProjectSubmit = async () => {
    const formErrors = validate();
    if (Object.keys(formErrors).length > 0) {
      setValidationErrors(formErrors);
      return;
    }

    if (!currentUser) {
      setError('You must be logged in to create a project.');
      return;
    }

    setIsCreatingProject(true);
    try {
      const projectData = {
        name: newProjectName,
        description: newProjectDescription || undefined,
        is_private: newProjectIsPrivate,
        tags: newProjectTags,
        stage: newProjectStage,
        priority: newProjectPriority,
      };
      const newProjectResponse = await createProject(projectData);
      
      // Transform the new project response to match our Project type
      const transformedNewProject = {
        ...newProjectResponse,
        id: newProjectResponse.id,
        name: newProjectResponse.name || 'Untitled Project',
        description: newProjectResponse.description || '',
        isPrivate: newProjectResponse.is_private ?? true,
        tags: newProjectResponse.tags || [],
        fileCount: newProjectResponse.file_count || 0,
        queryCount: newProjectResponse.query_count || 0,
        totalSize: newProjectResponse.total_size || 0,
        updatedDate: newProjectResponse.updated_at || new Date().toISOString(),
        stage: newProjectResponse.stage || 'Initiation',
        priority: newProjectResponse.priority || 'Medium',
      };
      
      // Add the new project to the list immediately
      setProjects(prevProjects => [transformedNewProject, ...prevProjects]);

      if (selectedTeamMembers.length > 0) {
        for (const user of selectedTeamMembers) {
          try {
            await addUserToProject({
              project_id: newProjectResponse.id,
              user_id: user.id
            });
          } catch (err) {
            console.error(`Error adding user ${user.id} to project:`, err);
          }
        }
      }
      
      setCreateDialogOpen(false);
      setNewProjectName('');
      setNewProjectDescription('');
      setNewProjectTags([]);
      setNewProjectIsPrivate(true);
      setSelectedTeamMembers(currentUser ? [currentUser] : []);
      setNewProjectStage(ProjectStage.Initiation);
      setNewProjectPriority(ProjectPriority.Medium);
      setValidationErrors({});
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Failed to create project.';
      setValidationErrors({ form: errorMessage }); // Display general error in the dialog
      console.error(err);
    } finally {
      setIsCreatingProject(false);
    }
  };

  const handleCloseCreateDialog = () => {
    setCreateDialogOpen(false);
    setNewProjectName('');
    setNewProjectDescription('');
    setNewProjectTags([]);
    setNewProjectIsPrivate(true);
    setSelectedTeamMembers(currentUser ? [currentUser] : []);
    setNewProjectStage(ProjectStage.Origination);
    setNewProjectPriority(ProjectPriority.Medium);
    setValidationErrors({});
  };

  const handleDeleteProject = async (projectId: string) => {
    // Optimistic deletion
    const originalProjects = [...projects];
    setProjects(projects.filter(p => p.id !== projectId));
    try {
      await deleteProject(projectId);
    } catch (err) {
      console.error('Failed to delete project:', err);
      setError('Failed to delete project. Please try again.');
      setProjects(originalProjects); // Revert on failure
    }
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Box sx={{ flexGrow: 1 }}>
      {/* Enhanced Header */}
      <Paper sx={{ mb: 3, p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4">Projects</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setCreateDialogOpen(true)}
        >
          New Project
        </Button>
      </Paper>

      <Paper sx={{ width: '100%', height: '85vh', display: 'flex', flexDirection: 'column', p: 3 }}>
        <Box sx={{ pb: 3 }}> {/* Added padding-bottom to separate filters from scrollable content */}
          {/* Enhanced Search and Filters */}
          <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
              <TextField
                placeholder="Search projects, descriptions, or tags..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" />
                    </InputAdornment>
                  ),
                  endAdornment: searchTerm && (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setSearchTerm('')}>
                        <ClearIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  minWidth: 300,
                  flex: 1,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
              />
              
              <Tooltip title="Toggle Filters">
                <IconButton
                  onClick={() => setFiltersOpen(!filtersOpen)}
                  color={hasActiveFilters ? 'primary' : 'default'}
                  sx={{
                    border: hasActiveFilters ? '2px solid' : '1px solid',
                    borderColor: hasActiveFilters ? 'primary.main' : 'divider',
                  }}
                >
                  <Badge badgeContent={hasActiveFilters ? '!' : 0} color="error">
                    <FilterIcon />
                  </Badge>
                </IconButton>
              </Tooltip>

              <Divider orientation="vertical" flexItem />

              <ToggleButtonGroup
                value={sortBy}
                exclusive
                onChange={(e, value) => {
                  if (value) {
                    const isAsc = sortBy.endsWith('asc');
                    setSortBy(isAsc ? value.replace('asc', 'desc') as ProjectSort : value.replace('desc', 'asc') as ProjectSort);
                  }
                }}
                size="small"
              >
               <ToggleButton value={ProjectSort.UPDATED_ASC}>
                  <Tooltip title="Sort by Last Updated">
                    <ScheduleIcon />
                  </Tooltip>
                </ToggleButton>
                <ToggleButton value={ProjectSort.NAME_ASC}>
                  <Tooltip title="Sort by Name">
                    <TagIcon />
                  </Tooltip>
                </ToggleButton>
                <ToggleButton value={ProjectSort.FILE_COUNT_ASC}>
                  <Tooltip title="Sort by File Count">
                    <FileIcon />
                  </Tooltip>
                </ToggleButton>
              </ToggleButtonGroup>

              <IconButton
                onClick={() => {
                  const isAsc = sortBy.endsWith('asc');
                  setSortBy(isAsc ? sortBy.replace('asc', 'desc') as ProjectSort : sortBy.replace('desc', 'asc') as ProjectSort);
                }}
                color="primary"
              >
                <Tooltip title={`Sort ${sortBy.endsWith('asc') ? 'Descending' : 'Ascending'}`}>
                  <SortIcon sx={{ transform: sortBy.endsWith('desc') ? 'rotate(180deg)' : 'none' }} />
                </Tooltip>
              </IconButton>

              <Divider orientation="vertical" flexItem />

              <ToggleButtonGroup
                value={viewMode}
                exclusive
                onChange={(e, value) => value && setViewMode(value)}
                size="small"
              >
                <ToggleButton value="grid">
                  <Tooltip title="Grid View">
                    <GridViewIcon />
                  </Tooltip>
                </ToggleButton>
                <ToggleButton value="list">
                  <Tooltip title="List View">
                    <ListViewIcon />
                  </Tooltip>
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>

            {/* Expandable Filters */}
            {filtersOpen && (
              <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                  <FormControl size="small" sx={{ minWidth: 120 }}>
                    <InputLabel>Priority</InputLabel>
                    <Select
                      value={filterPriority}
                      onChange={(e) => setFilterPriority(e.target.value as ProjectPriority | 'all')}
                      label="Priority"
                    >
                      <MenuItem value="all">All Priorities</MenuItem>
                      <MenuItem value={ProjectPriority.Low}>Low</MenuItem>
                      <MenuItem value={ProjectPriority.Medium}>Medium</MenuItem>
                      <MenuItem value={ProjectPriority.High}>High</MenuItem>
                    </Select>
                  </FormControl>

                  <FormControl size="small" sx={{ minWidth: 120 }}>
                    <InputLabel>Privacy</InputLabel>
                    <Select
                      value={filterPrivacy}
                      onChange={(e) => setFilterPrivacy(e.target.value as 'all' | 'private' | 'public')}
                      label="Privacy"
                    >
                      <MenuItem value="all">All Projects</MenuItem>
                      <MenuItem value="private">Private Only</MenuItem>
                      <MenuItem value="public">Public Only</MenuItem>
                    </Select>
                  </FormControl>

                  <Autocomplete
                    multiple
                    size="small"
                    options={allTags}
                    value={selectedTags}
                    onChange={(e, value) => setSelectedTags(value)}
                    renderInput={(params) => (
                      <TextField {...params} label="Filter by Tags" sx={{ minWidth: 200 }} />
                    )}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip variant="outlined" label={option} size="small" {...getTagProps({ index })} />
                      ))
                    }
                  />
                  <FormControl size="small" sx={{ minWidth: 120 }}>
                    <InputLabel>Stage</InputLabel>
                    <Select
                      value={filterStage}
                      onChange={(e) => setFilterStage(e.target.value as ProjectStage | 'all')}
                      label="Stage"
                    >
                      <MenuItem value="all">All Stages</MenuItem>
                      {Object.entries(stageDescriptions).map(([key, value]) => (
                        <MenuItem key={key} value={key} sx={{ whiteSpace: 'normal' }}>
                          <Typography component="div" sx={{ whiteSpace: 'normal' }}>
                            {toTitleCase(key)}: {value}
                          </Typography>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  {hasActiveFilters && (
                    <Button
                      startIcon={<ClearIcon />}
                      onClick={clearAllFilters}
                      size="small"
                      color="error"
                      variant="outlined"
                    >
                      Clear Filters
                    </Button>
                  )}
                </Box>
              </Box>
            )}
          </Paper>
        </Box>

        <Box sx={{ flexGrow: 1, overflowY: 'auto', p: 3, pt: 0 }}> {/* Added p:3 and pt:0 for consistent padding */}
          {/* Projects Display */}
          {loading ? (
            <>
              <PageHeaderSkeleton />
              <Box sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                gap: 3
              }}>
                {Array.from({ length: 8 }).map((_, index) => (
                  <ProjectCardSkeleton key={index} />
                ))}
              </Box>
            </>
          ) : error ? (
            <Alert severity="error" sx={{ borderRadius: 2 }}>{error}</Alert>
          ) : (
            <>
              {viewMode === 'grid' ? (
                <Box sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                  gap: 3
                }}>
                  {/* Create New Project Card */}
                  <Zoom in timeout={200}>
                    <Card
                      variant="outlined"
                      interactive
                      onClick={() => setCreateDialogOpen(true)}
                      sx={{
                        minHeight: '280px',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                        textAlign: 'center',
                        borderStyle: 'dashed',
                        borderWidth: 2,
                        transition: 'all 0.3s ease-in-out',
                        '&:hover': {
                          borderColor: 'primary.main',
                          bgcolor: 'primary.50',
                          transform: 'translateY(-4px)',
                          boxShadow: '0 8px 24px rgba(25, 118, 210, 0.15)',
                        }
                      }}
                    >
                      <AddIcon sx={{ fontSize: 56, color: 'primary.main', mb: 2 }} />
                      <Typography variant="h6" component="h2" sx={{ mb: 1, fontWeight: 600 }}>
                        Create New Project
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Start organizing your work with a new project
                      </Typography>
                    </Card>
                  </Zoom>

                  {/* Project Cards */}
                    {filteredProjects.map((project, index) => (
                    <Zoom in timeout={300 + index * 50} key={project.id}>
                      <Card
                        variant="interactive"
                        onClick={() => navigate(`/projects/${project.id}`)}
                        sx={{
                          minHeight: '280px',
                          position: 'relative',
                          transition: 'all 0.3s ease-in-out',
                          '&:hover': {
                            transform: 'translateY(-4px)',
                            boxShadow: '0 12px 32px rgba(0,0,0,0.15)',
                          }
                        }}
                      >
                        <Card.Header
                          title={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="h6" sx={{ fontWeight: 600, flex: 1 }}>
                                {project.name}
                              </Typography>
                            </Box>
                          }
                          action={
                            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', pt: 1 }}>
                               <Chip
                                 label={project.stage}
                                 size="small"
                                 color={getStageColor(project.stage) as any}
                                 variant="filled"
                               />
                               <Chip
                                 label={project.priority}
                                 size="small"
                                 color={getPriorityColor(project.priority) as any}
                                 variant="outlined"
                               />
                            </Box>
                          }
                        />
                        
                        <Card.Content sx={{ flex: 1 }}>
                          {project.description && (
                            <Typography variant="body2" color="text.secondary" sx={{
                              mb: 2,
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                              lineHeight: 1.4,
                            }}>
                              {project.description}
                            </Typography>
                          )}
                          
                          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
                            <Paper sx={{ p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                <FileIcon color="primary" fontSize="small" />
                                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                  {project.fileCount}
                                </Typography>
                              </Box>
                              <Typography variant="caption" color="text.secondary">
                                Files ({formatBytes(project.totalSize)})
                              </Typography>
                            </Paper>
                            
                            <Paper sx={{ p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                <SearchIcon color="success" fontSize="small" />
                                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                  {project.queryCount}
                                </Typography>
                              </Box>
                              <Typography variant="caption" color="text.secondary">
                                Queries
                              </Typography>
                            </Paper>
                          </Box>

                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
                            <Chip
                              icon={project.isPrivate ? <LockIcon fontSize="small" /> : <PublicIcon fontSize="small" />}
                              label={project.isPrivate ? 'Private' : 'Public'}
                              size="small"
                              variant="outlined"
                              color={project.isPrivate ? 'error' : 'success'}
                            />
                          </Box>
                        </Card.Content>
                        
                        <Card.Footer>
                          <Box sx={{ display: 'flex', gap: 0.5, flex: 1, overflow: 'hidden' }}>
                            {project.tags?.slice(0, 2).map((tag) => (
                              <Chip key={tag} label={tag} size="small" variant="outlined" />
                            ))}
                            {project.tags?.length > 2 && (
                              <Chip label={`+${project.tags.length - 2}`} size="small" />
                            )}
                          </Box>
                          <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap', ml: 1 }}>
                            {formatDate(project.updatedDate)}
                          </Typography>
                        </Card.Footer>
                      </Card>
                    </Zoom>
                  ))}
                </Box>
              ) : (
                // List View
                <Stack spacing={2}>
                 {filteredProjects.map((project, index) => (
                   <Fade in timeout={200} key={project.id}>
                     <MuiCard
                       variant="outlined"
                       sx={{
                         cursor: 'pointer',
                         transition: 'all 0.2s ease-in-out',
                         '&:hover': {
                           transform: 'translateX(4px)',
                           boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
                         }
                       }}
                       onClick={() => navigate(`/projects/${project.id}`)}
                     >
                       <Box sx={{ display: 'flex', alignItems: 'center', p: 2, gap: 2 }}>
                         <Box sx={{ flex: 1 }}>
                           <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                             <Typography variant="h6" sx={{ fontWeight: 600 }}>
                               {project.name}
                             </Typography>
                             <Chip label={project.stage} color={getStageColor(project.stage) as any} size="small" />
                           </Box>
                           <Typography variant="body2" color="text.secondary">
                             {project.description || 'No description provided.'}
                           </Typography>
                         </Box>
                         
                         <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, minWidth: '400px', justifyContent: 'space-between' }}>
                           <Box sx={{ textAlign: 'center' }}>
                             <Typography variant="caption" color="text.secondary">Files</Typography>
                             <Typography variant="body2">{project.fileCount}</Typography>
                           </Box>
                           <Box sx={{ textAlign: 'center' }}>
                             <Typography variant="caption" color="text.secondary">Queries</Typography>
                             <Typography variant="body2">{project.queryCount}</Typography>
                           </Box>
                           <Box sx={{ textAlign: 'center' }}>
                             <Typography variant="caption" color="text.secondary">Size</Typography>
                             <Typography variant="body2">{formatBytes(project.totalSize)}</Typography>
                           </Box>
                           <Box sx={{ textAlign: 'center' }}>
                             <Typography variant="caption" color="text.secondary">Updated</Typography>
                             <Typography variant="body2">{formatDate(project.updatedDate)}</Typography>
                           </Box>
                         </Box>

                         <CardActions sx={{ flexWrap: 'wrap', gap: 1 }}>
                           <Button
                             size="small"
                             variant="contained"
                             startIcon={<BusinessIcon />}
                             onClick={(e) => {
                               e.stopPropagation();
                               navigate(`/projects/${project.id}`);
                             }}
                           >
                             View Details
                           </Button>
                           <Button
                             size="small"
                             variant="outlined"
                             color="error"
                             startIcon={<DeleteIcon />}
                             onClick={(e) => {
                               e.stopPropagation();
                               handleDeleteProject(project.id);
                             }}
                           >
                             Delete
                           </Button>
                         </CardActions>
                       </Box>
                     </MuiCard>
                   </Fade>
                 ))}
               </Stack>
              )}
            </>
          )}
        </Box>
        <Box sx={{ width: '100%' }}>
          <TablePagination
            component="div"
            count={totalProjects}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25, 50, 100]}
          />
        </Box>
      </Paper>

      <Dialog
        open={createDialogOpen}
        onClose={handleCloseCreateDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: 'background.paper',
            borderRadius: 2,
          }
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FolderIcon />
            Create New Project
          </Box>
        </DialogTitle>
        <DialogContent>
          {validationErrors.form && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {validationErrors.form}
            </Alert>
          )}
          <Stack spacing={2} sx={{ mt: 2 }}>
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <TagIcon fontSize="small" /> Project Name *
              </Typography>
              <TextField
                fullWidth
                value={newProjectName}
                onChange={(e) => setNewProjectName(e.target.value)}
                required
                error={!!validationErrors.name}
                helperText={validationErrors.name}
              />
            </Box>
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <TextFieldsIcon fontSize="small" /> Description
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={3}
                value={newProjectDescription}
                onChange={(e) => setNewProjectDescription(e.target.value)}
                error={!!validationErrors.description}
                helperText={validationErrors.description}
              />
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormControl fullWidth>
                <InputLabel>Stage</InputLabel>
                <Select
                  value={newProjectStage}
                  onChange={(e) => setNewProjectStage(e.target.value as ProjectStage)}
                  label="Stage"
                  renderValue={(selected) => (
                    <Typography noWrap={false} sx={{ whiteSpace: 'normal' }}>
                      {`${toTitleCase(selected)}: ${stageDescriptions[selected as keyof typeof stageDescriptions]}`}
                    </Typography>
                  )}
                  MenuProps={{
                    PaperProps: {
                      style: {
                        maxHeight: 224,
                      },
                    },
                  }}
                >
                  {Object.entries(stageDescriptions).map(([key, value]) => (
                    <MenuItem key={key} value={key} sx={{ whiteSpace: 'normal' }}>
                      <Typography component="div" sx={{ whiteSpace: 'normal' }}>
                        {toTitleCase(key)}: {value}
                      </Typography>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel>Priority</InputLabel>
                <Select
                  value={newProjectPriority}
                  onChange={(e) => setNewProjectPriority(e.target.value as ProjectPriority)}
                  label="Priority"
                >
                  <MenuItem value={ProjectPriority.Low}>Low</MenuItem>
                  <MenuItem value={ProjectPriority.Medium}>Medium</MenuItem>
                  <MenuItem value={ProjectPriority.High}>High</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <GroupIcon fontSize="small" /> Team Members {newProjectIsPrivate && '*'}
              </Typography>
              <Autocomplete
                multiple
                options={users.filter(user => user.is_active)}
                loading={loadingUsers}
                getOptionLabel={(user) => user.email || ''}
                value={selectedTeamMembers}
                onChange={(event, newValue: User[]) => {
                  if (currentUser) {
                    const currentUserInSelection = newValue.find((u: User) => u.id === currentUser.id);
                    if (!currentUserInSelection) {
                      // Always keep the current user selected
                      setSelectedTeamMembers([currentUser, ...newValue.filter((u: User) => u.id !== currentUser.id)]);
                    } else {
                      setSelectedTeamMembers(newValue);
                    }
                  } else {
                    setSelectedTeamMembers(newValue);
                  }
                }}
                getOptionDisabled={(option) => option.id === currentUser?.id}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Add team members..."
                    error={!!validationErrors.team}
                    helperText={validationErrors.team}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingUsers ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      label={option.email}
                      {...getTagProps({ index })}
                      disabled={option.id === currentUser?.id}
                    />
                  ))
                }
                renderOption={(props, user) => (
                  <Box component="li" {...props}>
                    {user.email}
                  </Box>
                )}
              />
            </Box>
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <FilterIcon fontSize="small" /> Tags
              </Typography>
              <Autocomplete
                multiple
                freeSolo
                options={allTags}
                value={newProjectTags}
                onChange={(event, newValue) => setNewProjectTags(newValue as string[])}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Enter tags separated by commas"
                  />
                )}
              />
            </Box>
            <FormControlLabel
              control={
                <Switch
                  checked={newProjectIsPrivate}
                  onChange={(e) => setNewProjectIsPrivate(e.target.checked)}
                />
              }
              label={
                <Typography sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <LockIcon fontSize="small" /> Private project
                </Typography>
              }
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseCreateDialog}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCreateProjectSubmit}
            disabled={isCreatingProject || !newProjectName.trim() || (newProjectIsPrivate && selectedTeamMembers.length === 0)}
            startIcon={isCreatingProject ? <CircularProgress size={20} color="inherit" /> : <AddIcon />}
          >
            {isCreatingProject ? 'Creating...' : 'Create Project'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Projects;
